<template>
  <div>
    <v-main>
      <!-- หัวหน้า: pattern เดียวกับหน้าอื่น (breadcrumbs ซ้าย / ปุ่ม primary ขวา) -->
      <div class="pl-5">
        <v-row>
          <v-col cols="6">
            <v-breadcrumbs class="pl-0 pb-10" :items="breadcrumbs" divider=">"></v-breadcrumbs>
          </v-col>
          <v-col cols="6" align="right">
            <v-btn class="mt-6 mr-4" text color="primary" :disabled="!dataReady" @click="dialogSample = true">
              <v-icon left>mdi-play-circle-outline</v-icon>
              ฟังประกาศตัวอย่าง
            </v-btn>
            <v-btn class="mt-6 mr-10" color="primary" depressed :disabled="!dirtyCount || !dataReady" @click="save">
              <v-icon left>mdi-content-save</v-icon>
              บันทึก<span v-if="dirtyCount">&nbsp;({{ dirtyCount }})</span>
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <div class="pl-12 pr-12 col-md-12 ml-sm-auto col-lg-12 px-4">
        <div v-if="!dataReady" class="text-center">
          <waitingAlert></waitingAlert>
        </div>
        <div v-else class="queue-sound-setting">

          <!-- โหมด -->
          <v-card class="pa-4 mb-4" width="100%">
            <div class="d-flex align-center flex-wrap">
              <span class="subtitle-2 mr-4">เสียงประกาศบนจอ</span>
              <v-btn-toggle :key="modeToggleKey" :value="mode" mandatory dense color="primary" class="mode-toggle" @change="onModeToggle">
                <v-btn value="legacy" outlined class="px-4"><v-icon left small>mdi-server</v-icon>เสียงกลางของระบบ</v-btn>
                <v-btn value="import" outlined class="px-4"><v-icon left small>mdi-store</v-icon>เสียงที่ร้านอัปโหลดเอง</v-btn>
              </v-btn-toggle>
            </div>
            <div class="announce-formula mt-4">
              <span class="announce-formula__part">1 ท่อนเปิด</span><span class="announce-formula__plus">+</span>
              <span class="announce-formula__part">2 เลขคิว</span><span class="announce-formula__plus">+</span>
              <span class="announce-formula__part">3 ที่ห้อง</span><span class="announce-formula__plus">+</span>
              <span class="announce-formula__part">4 ชื่อห้อง</span>
              <span class="body-2 grey--text text--darken-1 ml-3">ท่อนเปิด/เลขคิวที่ยังไม่อัปโหลด จอใช้เสียงกลางแทน · ที่ห้อง/ชื่อห้องที่ไม่มี จอข้าม</span>
            </div>
            <v-alert v-if="mode === 'import' && missingRoomCount" dense text type="warning" class="mt-4 mb-0">
              ยังมี {{ missingRoomCount }} ห้องที่ไม่มีเสียงไทย — จอจะประกาศแค่ท่อนเปิด + เลขคิว
            </v-alert>
          </v-card>

          <!-- ภาษา = แท็บ ทุกท่อนข้างล่างเป็นของภาษาที่เลือก -->
          <v-card class="mb-4" width="100%">
            <v-tabs v-model="activeLang" background-color="transparent" color="primary" show-arrows>
              <v-tab v-for="lang in langColumns" :key="lang.code" :href="'#' + lang.code">
                {{ lang.label }}
                <v-chip x-small class="ml-2" :color="langProgress(lang.code).color" text-color="white">{{ langProgress(lang.code).text }}</v-chip>
                <v-btn v-if="lang.removable" icon x-small class="ml-1" :aria-label="'ลบภาษา ' + lang.label" title="ลบภาษานี้" @click.stop.prevent="removeLang(lang.code)">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </v-tab>
              <v-btn text small color="primary" class="align-self-center ml-2" @click="dialogLang = true">
                <v-icon left small>mdi-plus</v-icon>เพิ่มภาษา
              </v-btn>
            </v-tabs>
            <v-divider></v-divider>

            <v-card-text class="pa-4">
              <!-- 1 ท่อนเปิด -->
              <section class="segment">
                <div class="segment__head">
                  <span class="segment__no">1</span>
                  <div>
                    <div class="subtitle-1 font-weight-medium">ท่อนเปิด</div>
                    <div class="body-2 grey--text text--darken-1">เช่น "ขอเชิญคิว" — 1 ไฟล์</div>
                  </div>
                </div>
                <div class="segment__body">
                  <sound-cell :cell="getCell(prefixRow, activeLang)" :accept="acceptTypes" :fallback="systemFallbackLabel"
                    @file="onFileChange(prefixRow, activeLang, $event)"
                    @play="playCell(prefixRow, activeLang)"
                    @remove="removeCell(prefixRow, activeLang)"
                    @restore="restoreCell(prefixRow, activeLang)"></sound-cell>
                </div>
              </section>

              <v-divider class="my-4"></v-divider>

              <!-- 2 เลขคิว A–Z 0–9 -->
              <section class="segment">
                <div class="segment__head">
                  <span class="segment__no">2</span>
                  <div>
                    <div class="subtitle-1 font-weight-medium">เลขคิว
                      <v-chip x-small class="ml-2" :color="queueCharProgress.done === 36 ? 'success' : 'grey'" text-color="white">{{ queueCharProgress.done }}/36</v-chip>
                    </div>
                    <div class="body-2 grey--text text--darken-1">ตัวอักษร A–Z และตัวเลข 0–9 ตัวละ 1 ไฟล์ — จอต่อเป็นเลขคิวเอง (เอ ศูนย์ ศูนย์ หนึ่ง) · ต้องครบทั้ง 36 ตัว ไม่ครบจอใช้เสียงกลาง</div>
                  </div>
                  <v-spacer></v-spacer>
                  <v-btn small outlined color="primary" @click="pickMulti('queue_char')">
                    <v-icon left small>mdi-upload-multiple</v-icon>อัปโหลดหลายไฟล์
                  </v-btn>
                </div>
                <div class="segment__body">
                  <div class="char-grid">
                    <v-menu v-for="ch in queueChars" :key="ch" offset-y :disabled="!charHasMenu(ch)">
                      <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="char-tile" :class="charTileClass(ch)" v-bind="attrs" v-on="on"
                          :title="charTitle(ch)" :aria-label="'ตัว ' + ch + ' — ' + charTitle(ch)" @click="onCharClick(ch)">
                          <span class="char-tile__char">{{ ch }}</span>
                          <v-icon x-small class="char-tile__icon">{{ charIcon(ch) }}</v-icon>
                        </button>
                      </template>
                      <v-list dense>
                        <v-list-item v-if="cellHas(charRow(ch), activeLang)" @click="playCell(charRow(ch), activeLang)">
                          <v-list-item-icon class="mr-3"><v-icon small color="primary">mdi-play-circle-outline</v-icon></v-list-item-icon>
                          <v-list-item-title>ฟัง</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="pickCharFile(ch)">
                          <v-list-item-icon class="mr-3"><v-icon small>mdi-upload-outline</v-icon></v-list-item-icon>
                          <v-list-item-title>เปลี่ยนไฟล์</v-list-item-title>
                        </v-list-item>
                        <v-list-item v-if="getCell(charRow(ch), activeLang).removed" @click="restoreCell(charRow(ch), activeLang)">
                          <v-list-item-icon class="mr-3"><v-icon small>mdi-undo-variant</v-icon></v-list-item-icon>
                          <v-list-item-title>ยกเลิกการลบ</v-list-item-title>
                        </v-list-item>
                        <v-list-item v-else @click="removeCell(charRow(ch), activeLang)">
                          <v-list-item-icon class="mr-3"><v-icon small color="error">mdi-delete-outline</v-icon></v-list-item-icon>
                          <v-list-item-title class="error--text">ลบ</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                  <div class="caption grey--text text--darken-1 mt-2">กดตัวที่มีเสียงเพื่อฟัง / เปลี่ยนไฟล์ / ลบ · กดตัวที่ยังไม่มีเพื่ออัปโหลด · ชื่อไฟล์ = ตัวอักษร (A.mp3, 7.mp3) ระบบจับคู่ให้เอง</div>
                </div>
              </section>

              <v-divider class="my-4"></v-divider>

              <!-- 3 ที่ห้อง -->
              <section class="segment">
                <div class="segment__head">
                  <span class="segment__no">3</span>
                  <div>
                    <div class="subtitle-1 font-weight-medium">ที่ห้อง</div>
                    <div class="body-2 grey--text text--darken-1">คำเชื่อมก่อนชื่อห้อง เช่น "ที่ห้อง" / "เชิญที่" — 1 ไฟล์ ใช้กับทุกห้อง · ไม่อัปโหลด = จอข้ามท่อนนี้ (ชื่อห้องต้องอัดคำว่า "ที่ห้อง" มาเอง)</div>
                  </div>
                </div>
                <div class="segment__body">
                  <sound-cell :cell="getCell(connectorRow, activeLang)" :accept="acceptTypes" fallback="ไม่มี — จอข้ามท่อนนี้"
                    @file="onFileChange(connectorRow, activeLang, $event)"
                    @play="playCell(connectorRow, activeLang)"
                    @remove="removeCell(connectorRow, activeLang)"
                    @restore="restoreCell(connectorRow, activeLang)"></sound-cell>
                </div>
              </section>

              <v-divider class="my-4"></v-divider>

              <!-- 4 ชื่อห้อง -->
              <section class="segment">
                <div class="segment__head">
                  <span class="segment__no">4</span>
                  <div>
                    <div class="subtitle-1 font-weight-medium">ชื่อห้อง</div>
                    <div class="body-2 grey--text text--darken-1">อัดเฉพาะชื่อห้อง เช่น "ตรวจ 3 ค่ะ" (ถ้าไม่ได้อัปโหลดท่อน "ที่ห้อง" ให้อัดรวม "ที่ห้องตรวจ 3 ค่ะ") ห้องละ 1 ไฟล์ · ชื่อไฟล์ = ชื่อห้อง (ห้องตรวจ 1.mp3) จับคู่ให้เอง · ห้องชื่อเดียวกันในแผนกอื่นใช้ไฟล์ร่วมกันได้ ไม่ต้องอัปโหลดซ้ำ</div>
                  </div>
                  <v-spacer></v-spacer>
                  <v-btn small outlined color="primary" @click="pickMulti('room')">
                    <v-icon left small>mdi-upload-multiple</v-icon>อัปโหลดหลายไฟล์
                  </v-btn>
                </div>
                <div class="segment__body">
                  <v-select v-model="selectedFlowId" :items="flowItems" label="แผนก" dense outlined hide-details class="flow-select mb-3"></v-select>
                  <v-simple-table v-if="flowRows.length" dense class="sound-table">
                    <thead>
                      <tr><th>ห้อง</th><th>เสียง ({{ activeLangLabel }})</th></tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in flowRows" :key="row.key" :class="{ 'row-orphan': row.orphan }">
                        <td>
                          <div class="body-2">{{ row.label }}</div>
                          <div v-if="row.labelEn" class="caption grey--text">{{ row.labelEn }}</div>
                          <v-chip v-if="row.orphan" x-small color="error" outlined class="mt-1">ไม่พบห้องนี้ใน Flow แล้ว</v-chip>
                        </td>
                        <td>
                          <sound-cell :cell="getCell(row, activeLang)" :accept="acceptTypes" :fallback="sharedRoomLabel(row, activeLang)"
                            @file="onFileChange(row, activeLang, $event)"
                            @play="playCell(row, activeLang)"
                            @test="testSequence(row, activeLang)"
                            @remove="removeCell(row, activeLang)"
                            @restore="restoreCell(row, activeLang)"></sound-cell>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                  <div v-else-if="selectedFlowId" class="body-2 grey--text py-3">แผนกนี้ยังไม่ได้เปิดห้องบริการ — ไปตั้งที่ <router-link to="/Master/Flow">จัดการ บริการ</router-link> ก่อน</div>

                  <div class="subtitle-2 mt-5 mb-2">จุดส่งต่อ (ใช้ร่วมทุกแผนก)</div>
                  <v-simple-table v-if="forwardRows.length" dense class="sound-table">
                    <thead>
                      <tr><th>จุดส่งต่อ</th><th>เสียง ({{ activeLangLabel }})</th></tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in forwardRows" :key="row.key">
                        <td>
                          <div class="body-2">{{ row.label }}</div>
                          <div v-if="row.labelEn" class="caption grey--text">{{ row.labelEn }}</div>
                        </td>
                        <td>
                          <sound-cell :cell="getCell(row, activeLang)" :accept="acceptTypes" fallback=""
                            @file="onFileChange(row, activeLang, $event)"
                            @play="playCell(row, activeLang)"
                            @test="testSequence(row, activeLang)"
                            @remove="removeCell(row, activeLang)"
                            @restore="restoreCell(row, activeLang)"></sound-cell>
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                  <div v-else class="body-2 grey--text py-3">ร้านนี้ยังไม่มีจุดส่งต่อ — สร้างได้ที่ <router-link to="/Master/Flow">จัดการ บริการ</router-link></div>
                </div>
              </section>
            </v-card-text>
          </v-card>

          <input ref="multiInput" type="file" multiple class="d-none" :accept="acceptTypes" @change="onMultiFiles">
          <audio ref="player" class="d-none"></audio>

          <!-- dialog: เพิ่มภาษา -->
          <v-dialog v-model="dialogLang" max-width="460">
            <v-card>
              <v-card-title class="subtitle-1">เพิ่มภาษา / ผูกกับภาษาที่จอง</v-card-title>
              <v-card-text>
                <v-select v-model="newLang" :items="langChoices" item-text="label" item-value="code" label="เลือกภาษา" dense outlined hide-details class="mb-3"></v-select>
                <v-text-field v-model="newLangCustom" label="หรือพิมพ์รหัสภาษาเอง (เช่น de)" dense outlined hide-details class="mb-3"></v-text-field>
                <v-text-field v-model="newLangBookingNo" type="number" min="2" label="ตรงกับภาษาที่จองเลข (bookingLanguage)" dense outlined persistent-hint
                  :error-messages="newLangBookingNoError ? [newLangBookingNoError] : []"
                  hint="เลขที่ตู้คิว/หน้าจองส่งมาเมื่อลูกค้าเลือกภาษานี้ (0 = ไทย, 1 = อังกฤษ) — เว้นว่าง = ยังไม่ผูก จอใช้เสียงไทย"></v-text-field>
              </v-card-text>
              <v-card-actions class="px-6 pb-4">
                <v-spacer></v-spacer>
                <v-btn text @click="dialogLang = false">ยกเลิก</v-btn>
                <v-btn color="primary" depressed :disabled="!(newLangCustom || newLang) || !!newLangBookingNoError" @click="addLang">บันทึก</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- dialog: ยืนยันการจับคู่ไฟล์หลายไฟล์ -->
          <v-dialog v-model="dialogMatch" max-width="720" scrollable>
            <v-card>
              <v-card-title class="subtitle-1">จับคู่ไฟล์ → {{ activeLangLabel }}</v-card-title>
              <v-card-text style="max-height: 60vh">
                <v-simple-table dense>
                  <thead><tr><th>ไฟล์</th><th>ใส่ให้</th></tr></thead>
                  <tbody>
                    <tr v-for="(m, i) in matchList" :key="i">
                      <td class="body-2">{{ m.file.name }} <span class="caption grey--text">({{ (m.file.size / 1024).toFixed(0) }} KB)</span></td>
                      <td>
                        <v-select v-model="m.key" :items="matchTargets" item-text="label" item-value="key" dense outlined hide-details
                          placeholder="ไม่พบเป้าหมาย — เลือกเอง" :error="!m.key"></v-select>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card-text>
              <v-card-actions class="px-6 pb-4">
                <span class="caption grey--text text--darken-1">{{ matchedCount }}/{{ matchList.length }} ไฟล์จับคู่ได้</span>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogMatch = false">ยกเลิก</v-btn>
                <v-btn color="primary" depressed :disabled="!matchedCount" @click="applyMatches">ใส่ไฟล์ ({{ matchedCount }})</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- dialog: ฟังประกาศตัวอย่างทั้งประโยค -->
          <v-dialog v-model="dialogSample" max-width="480">
            <v-card>
              <v-card-title class="subtitle-1">ฟังประกาศตัวอย่าง ({{ activeLangLabel }})</v-card-title>
              <v-card-text>
                <v-text-field v-model="sampleQueue" label="เลขคิว" dense outlined hide-details class="mb-3" maxlength="6"></v-text-field>
                <v-select v-model="sampleRoomKey" :items="roomTargets" item-text="label" item-value="key" label="ห้อง" dense outlined hide-details></v-select>
                <div class="mt-4">
                  <div v-for="seg in samplePlan" :key="seg.no" class="d-flex align-center body-2 py-1">
                    <span class="segment__no segment__no--sm mr-2">{{ seg.no }}</span>
                    <span class="mr-2">{{ seg.label }}</span>
                    <v-chip x-small :color="seg.color" text-color="white">{{ seg.source }}</v-chip>
                  </div>
                </div>
              </v-card-text>
              <v-card-actions class="px-6 pb-4">
                <v-spacer></v-spacer>
                <v-btn text @click="dialogSample = false">ปิด</v-btn>
                <v-btn color="primary" depressed @click="playSample"><v-icon left>mdi-play</v-icon>เล่น</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </div>
    </v-main>
  </div>
</template>

<script>
import axios from 'axios' // api
import waitingAlert from '../waitingAlert.vue'

// เสียงกลางของระบบ (ชุดเดียวกับที่จอ TV ใช้) — ใช้แทนท่อนที่ร้านยังไม่อัปโหลด; ภาษาที่ 3 ใช้ชุดไทย
const SYSTEM_SOUND = {
  prefix: { th: 'https://storage.googleapis.com/thaiairway-sound/prefix1.wav', en: 'https://storage.googleapis.com/thaiairway-sound/prefix_en.mp3' },
  queue: { th: code => 'https://storage.googleapis.com/thaiairway-sound/' + code + '.mp3', en: code => 'https://storage.googleapis.com/thaiairway-sound/en/' + code + '.mp3' }
}
const QUEUE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.split('')
const LANG_NAMES = { th: 'ไทย', en: 'English', zh: 'จีน', ja: 'ญี่ปุ่น', ko: 'เกาหลี', my: 'พม่า', lo: 'ลาว', km: 'เขมร', vi: 'เวียดนาม', ru: 'รัสเซีย', de: 'เยอรมัน', fr: 'ฝรั่งเศส' }
const EMPTY_CELL = { url: null, fileName: null, file: null, previewUrl: null, removed: false, dirty: false }

// ช่องเสียง 1 ไฟล์: ปุ่ม ฟัง / (ทดสอบรวม) / อัปโหลด / ลบ-คืนค่า + สถานะ
const SoundCell = {
  name: 'SoundCell',
  props: ['cell', 'accept', 'fallback'],
  computed: {
    hasSound () { return !!(this.cell && (this.cell.url || this.cell.file)) },
    canPlay () { return this.hasSound || (this.fallback || '').indexOf('ใช้ไฟล์') === 0 },
    status () {
      if (this.cell && this.cell.file) return { icon: 'mdi-circle-medium', color: 'orange', text: this.cell.file.name + ' · ยังไม่บันทึก' }
      if (this.cell && this.cell.url) return { icon: 'mdi-check-circle', color: 'success', text: this.cell.fileName || 'ของร้าน' }
      if (this.cell && this.cell.removed) return { icon: 'mdi-delete-clock-outline', color: 'error', text: 'จะลบเมื่อบันทึก' }
      if (this.fallback) return { icon: (this.fallback.indexOf('ใช้ไฟล์') === 0 ? 'mdi-link-variant' : 'mdi-circle-medium'), color: (this.fallback.indexOf('ใช้ไฟล์') === 0 ? 'primary' : 'grey'), text: this.fallback }
      return { icon: 'mdi-alert-circle-outline', color: 'grey', text: 'ยังไม่มีเสียง' }
    }
  },
  methods: {
    pickFile () { this.$refs.input.click() },
    onInput (event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (file) this.$emit('file', file)
    }
  },
  template: `
    <div class="sound-cell">
      <div class="sound-cell__actions">
        <v-btn icon small :disabled="!canPlay" aria-label="ฟัง" title="ฟัง" @click="$emit('play')"><v-icon small :color="canPlay ? 'primary' : ''">mdi-play-circle-outline</v-icon></v-btn>
        <v-btn v-if="$listeners.test" icon small :disabled="!canPlay" aria-label="ฟังทั้งประโยคกับห้องนี้" title="ฟังทั้งประโยค (ขอเชิญคิว A001 + ห้องนี้)" @click="$emit('test')"><v-icon small :color="canPlay ? 'primary' : ''">mdi-bullhorn-outline</v-icon></v-btn>
        <v-btn text small color="primary" class="px-2" :aria-label="hasSound ? 'เปลี่ยนไฟล์เสียง' : 'อัปโหลดไฟล์เสียง'" @click="pickFile"><v-icon small left>mdi-upload-outline</v-icon>{{ hasSound ? 'เปลี่ยนไฟล์' : 'อัปโหลด' }}</v-btn>
        <v-btn v-if="cell && cell.removed" icon small aria-label="ยกเลิกการลบ" title="ยกเลิกการลบ" @click="$emit('restore')"><v-icon small>mdi-undo-variant</v-icon></v-btn>
        <v-btn v-else icon small :disabled="!hasSound" aria-label="ลบเสียง" title="ลบ" @click="$emit('remove')"><v-icon small :color="hasSound ? 'error' : ''">mdi-delete-outline</v-icon></v-btn>
        <input ref="input" type="file" class="d-none" :accept="accept" @change="onInput">
      </div>
      <div class="sound-cell__status text-truncate" :title="status.text">
        <v-icon x-small :color="status.color" class="mr-1">{{ status.icon }}</v-icon><span :class="status.color + '--text'">{{ status.text }}</span>
      </div>
    </div>
  `
}

export default {
  name: 'QueueSoundSetting',
  components: { waitingAlert, 'sound-cell': SoundCell },
  data () {
    return {
      dataReady: false,
      breadcrumbs: [
        { text: 'Home', disabled: false, href: '/Core/Home' },
        { text: 'ตั้งค่าเสียงเรียกคิว', disabled: true, href: '/BeFood/SettingQueueSound' }
      ],
      shopId: this.$session.getAll().data.shopId,
      userName: this.$session.getAll().data.userName,
      mode: 'legacy',
      modeToggleKey: 0,
      langs: [],
      langBookingNo: {},
      activeLang: 'th',
      acceptTypes: '.mp3,.wav,.m4a,audio/mpeg,audio/wav,audio/mp4',
      queueChars: QUEUE_CHARS,
      flows: [],
      forwardQueues: [],
      selectedFlowId: null,
      // sounds[key][lang] = cell — key = sourceType:sourceId:servicePointKey
      sounds: {},
      savedSounds: {},
      orphanKeys: [],
      previewUrls: [],
      pendingCharRow: null,
      dialogLang: false,
      newLang: null,
      newLangCustom: '',
      newLangBookingNo: '',
      dialogMatch: false,
      matchList: [],
      matchScope: 'room',
      dialogSample: false,
      sampleQueue: 'A001',
      sampleRoomKey: null
    }
  },
  computed: {
    prefixRow () { return this.buildRow('prefix', 'prefix', '', '') },
    connectorRow () { return this.buildRow('connector', 'connector', '', '') },
    langColumns () {
      return ['th', 'en'].concat(this.langs).map(code => ({
        code: code,
        removable: code !== 'th' && code !== 'en',
        label: (LANG_NAMES[code] ? LANG_NAMES[code] + (code === 'th' || code === 'en' ? '' : ' (' + code + ')') : code.toUpperCase()) +
          (this.langBookingNo[code] ? ' · จองเลข ' + this.langBookingNo[code] : '')
      }))
    },
    activeLangLabel () {
      const found = this.langColumns.filter(l => l.code === this.activeLang)[0]
      return found ? found.label : this.activeLang
    },
    langChoices () {
      return Object.keys(LANG_NAMES).filter(code => code !== 'th' && code !== 'en').map(code => ({
        code: code,
        label: LANG_NAMES[code] + ' (' + code + ')' + (this.langs.indexOf(code) > -1 ? ' — มีแล้ว แก้เลขที่จอง' : '')
      }))
    },
    queueSoundLangsValue () {
      return this.langs.map(code => code + (this.langBookingNo[code] ? ':' + this.langBookingNo[code] : '')).join(',')
    },
    newLangBookingNoError () {
      const number = String(this.newLangBookingNo || '').trim()
      if (!number) return ''
      if (!/^\d+$/.test(number) || number === '0' || number === '1') return 'ใช้ได้ตั้งแต่ 2 ขึ้นไป (0 = ไทย, 1 = อังกฤษ)'
      const code = String(this.newLangCustom || this.newLang || '').trim().toLowerCase()
      const owner = this.langs.filter(item => item !== code && String(this.langBookingNo[item]) === number)[0]
      return owner ? 'เลข ' + number + ' ผูกกับภาษา ' + owner + ' อยู่แล้ว' : ''
    },
    systemFallbackLabel () {
      return this.activeLang === 'th' || this.activeLang === 'en' ? 'ใช้เสียงกลางของระบบ' : 'ใช้เสียงกลางของระบบ (ไทย)'
    },
    flowItems () { return this.flows.map(flow => ({ text: flow.flowName, value: String(flow.flowId) })) },
    flowRows () {
      const flow = this.flows.filter(item => String(item.flowId) === String(this.selectedFlowId))[0]
      if (!flow) return []
      const rows = this.roomsOfFlow(flow)
      const known = rows.map(row => row.key)
      this.orphanKeys
        .filter(key => key.indexOf('flow:' + flow.flowId + ':') === 0 && known.indexOf(key) === -1)
        .forEach(key => {
          const row = this.buildRow('flow', flow.flowId, key.split(':').slice(2).join(':'), '')
          row.orphan = true
          rows.push(row)
        })
      return rows
    },
    forwardRows () {
      return this.forwardQueues.map(item => {
        const row = this.buildRow('forward_queue', item.id, '', '')
        row.label = item.forwardQueueNameTh || item.forwardQueueNameEn
        row.labelEn = item.forwardQueueNameEn
        return row
      })
    },
    allRoomRows () {
      let rows = []
      this.flows.forEach(flow => {
        rows = rows.concat(this.roomsOfFlow(flow).map(r => Object.assign(r, { flowName: flow.flowName })))
      })
      return rows.concat(this.forwardRows)
    },
    roomTargets () {
      return this.allRoomRows.map(r => ({ key: r.key, label: (r.flowName ? r.flowName + ' › ' : 'จุดส่งต่อ › ') + r.label }))
    },
    matchTargets () {
      if (this.matchScope === 'queue_char') return QUEUE_CHARS.map(ch => ({ key: 'queue_char:' + ch + ':', label: 'ตัว ' + ch }))
      return [{ key: 'prefix:prefix:', label: 'ท่อนเปิด' }, { key: 'connector:connector:', label: 'ที่ห้อง' }].concat(this.roomTargets)
    },
    matchedCount () { return this.matchList.filter(m => m.key).length },
    queueCharProgress () {
      return { done: QUEUE_CHARS.filter(ch => this.cellHas(this.charRow(ch), this.activeLang)).length }
    },
    dirtyCount () {
      let count = 0
      Object.keys(this.sounds).forEach(key => Object.keys(this.sounds[key]).forEach(lang => { if (this.sounds[key][lang].dirty) count++ }))
      return count
    },
    missingRoomCount () {
      return this.allRoomRows.filter(r => !this.cellHas(r, 'th') && !this.sharedRoom(r, 'th')).length
    },
    samplePlan () {
      const lang = this.activeLang
      const prefixOwn = this.cellHas(this.prefixRow, lang)
      const code = String(this.sampleQueue || 'A001').toUpperCase()
      const chars = code.split('')
      const queueOwn = chars.length > 0 && chars.every(ch => this.cellHas(this.charRow(ch), lang))
      const roomRow = this.allRoomRows.filter(r => r.key === this.sampleRoomKey)[0]
      const roomShared = roomRow && !this.cellHas(roomRow, lang) ? this.sharedRoom(roomRow, lang) : null
      const roomOwn = !!(roomRow && (this.cellHas(roomRow, lang) || roomShared))
      const connectorOwn = roomOwn && this.cellHas(this.connectorRow, lang)
      const sys = (lang === 'th' || lang === 'en') ? 'เสียงกลาง' : 'เสียงกลาง (ไทย)'
      return [
        { no: 1, label: 'ท่อนเปิด', source: prefixOwn ? 'ของร้าน' : sys, color: prefixOwn ? 'success' : 'grey' },
        { no: 2, label: 'เลขคิว ' + code, source: queueOwn ? 'ของร้าน (ต่อทีละตัว)' : sys, color: queueOwn ? 'success' : 'grey' },
        { no: 3, label: 'ที่ห้อง', source: connectorOwn ? 'ของร้าน' : 'ข้าม', color: connectorOwn ? 'success' : 'grey' },
        { no: 4, label: 'ชื่อห้อง ' + (roomRow ? roomRow.label : '-'), source: roomOwn ? (roomShared ? 'ของร้าน (ไฟล์ของแผนก ' + roomShared.flowName + ')' : 'ของร้าน') : 'ยังไม่มี — เงียบ', color: roomOwn ? 'success' : 'error' }
      ]
    }
  },
  watch: {
    newLang (code) {
      this.newLangBookingNo = (code && this.langBookingNo[code]) || ''
    }
  },
  async mounted () {
    await this.getData()
  },
  beforeDestroy () {
    this.previewUrls.forEach(url => URL.revokeObjectURL(url))
  },
  methods: {
    // swal2 ใน repo นี้: confirm resolve เป็น true (หรือ {value}) ส่วน cancel reject → ห่อไว้ให้คืน boolean
    async confirmSwal (options) {
      try {
        const result = await this.$swal(Object.assign({
          type: 'question', showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#b3b1ab', confirmButtonText: 'ใช่', cancelButtonText: 'ไม่'
        }, options))
        return !!(result && (result.value || result === true))
      } catch (error) {
        return false
      }
    },
    buildRow (sourceType, sourceId, servicePointKey, labelEn) {
      // ชื่อห้องใน Flow ~30% มีช่องว่างหัว/ท้าย (59/197 flow) — trim ให้ key ตรงกับที่จอ TV เทียบ (จอ trim servicePoint)
      servicePointKey = String(servicePointKey || '').trim()
      return {
        sourceType: sourceType,
        sourceId: String(sourceId),
        servicePointKey: servicePointKey,
        key: sourceType + ':' + sourceId + ':' + servicePointKey,
        label: servicePointKey,
        labelEn: labelEn,
        orphan: false
      }
    },
    charRow (ch) { return this.buildRow('queue_char', ch, '', '') },
    roomsOfFlow (flow) {
      const seen = {}
      return this.parseServicePointCountGlobal(flow.servicePointCount)
        .filter(point => point.textTh || point.textEn)
        .map(point => this.buildRow('flow', flow.flowId, point.textTh || point.textEn, point.textEn))
        .filter(row => { if (seen[row.key]) return false; seen[row.key] = true; return true }) // กัน key ซ้ำ (v-for)
    },
    getCell (row, lang) {
      return (this.sounds[row.key] && this.sounds[row.key][lang]) || EMPTY_CELL
    },
    cellHas (row, lang) {
      const cell = this.getCell(row, lang)
      return !!(cell.url || cell.file)
    },
    setCell (row, lang, cell) {
      if (!this.sounds[row.key]) this.$set(this.sounds, row.key, {})
      this.$set(this.sounds[row.key], lang, Object.assign({}, EMPTY_CELL, cell))
    },
    langProgress (lang) {
      const roomsDone = this.allRoomRows.filter(r => this.cellHas(r, lang)).length
      const chars = QUEUE_CHARS.filter(ch => this.cellHas(this.charRow(ch), lang)).length
      const prefix = this.cellHas(this.prefixRow, lang) ? 1 : 0
      const connector = this.cellHas(this.connectorRow, lang) ? 1 : 0
      const total = this.allRoomRows.length + 36 + 2
      const done = roomsDone + chars + prefix + connector
      return { text: done + '/' + total, color: done === 0 ? 'grey' : (done === total ? 'success' : 'orange') }
    },
    async getData () {
      this.dataReady = false
      this.sounds = {}
      this.orphanKeys = []
      try {
        const [flowRes, fqRes, soundRes] = await Promise.all([
          axios.get(this.$DNS_IP + '/flow/get?shopId=' + this.shopId + '&storeFrontCheck=True&isShow=all'),
          axios.get(this.$DNS_IP + '/forward_queue/get?shopId=' + this.shopId),
          axios.get(this.$DNS_IP + '/service_point_sound/get?shopId=' + this.shopId)
        ])
        this.flows = (Array.isArray(flowRes.data) ? flowRes.data : [])
          .filter(item => item.isShow !== 'False' && (item.servicePointStatus === 'True' || String(item.servicePointCount || '').indexOf('textTh') > -1))
        this.forwardQueues = (Array.isArray(fqRes.data) ? fqRes.data : []).filter(item => (item.isShow || 'True') === 'True')
        const sound = soundRes.data || {}
        this.mode = sound.queueSoundMode === 'import' ? 'import' : 'legacy'
        this.langs = []
        this.langBookingNo = {}
        String(sound.queueSoundLangs || '').split(',').map(part => part.trim().toLowerCase()).filter(part => part).forEach(part => {
          const code = part.split(':')[0].trim()
          if (!code || this.langs.indexOf(code) > -1) return
          this.langs.push(code)
          this.$set(this.langBookingNo, code, (part.split(':')[1] || '').trim())
        })
        const knownKeys = []
        this.flows.forEach(flow => this.roomsOfFlow(flow).forEach(r => knownKeys.push(r.key)))
        const namesByKey = {}
        ;(sound.main || []).forEach(item => {
          const row = this.buildRow(item.sourceType, item.sourceId, item.servicePointKey, '')
          let names = {}
          try { names = JSON.parse(item.soundFileNameAll || '{}') } catch (error) { names = {} }
          namesByKey[row.key] = names
          if (item.soundTh) this.setCell(row, 'th', { url: item.soundTh, fileName: names.th })
          if (item.soundEn) this.setCell(row, 'en', { url: item.soundEn, fileName: names.en })
          if (item.sourceType === 'flow' && knownKeys.indexOf(row.key) === -1) this.orphanKeys.push(row.key)
        })
        ;(sound.lang || []).forEach(item => {
          const row = this.buildRow(item.sourceType, item.sourceId, item.servicePointKey, '')
          const names = namesByKey[row.key] || {}
          this.setCell(row, item.langCode, { url: item.soundUrl, fileName: names[item.langCode] })
          if (this.langs.indexOf(item.langCode) === -1) this.langs.push(item.langCode)
        })
        this.savedSounds = JSON.parse(JSON.stringify(this.sounds))
        if (!this.selectedFlowId && this.flowItems.length) this.selectedFlowId = this.flowItems[0].value
        if (!this.sampleRoomKey && this.allRoomRows.length) this.sampleRoomKey = this.allRoomRows[0].key
      } catch (error) {
        console.log('getData error', error)
        this.$swal({ type: 'error', title: 'โหลดข้อมูลไม่สำเร็จ', text: String(error) })
      }
      this.dataReady = true
    },
    // ── ไฟล์ (ต้อง ≤ 5 MB — server เช็คซ้ำแล้วบีบอัดเป็น mp3 ก่อนเก็บ)
    checkFileSize (file) {
      if (file.size > 5 * 1024 * 1024) {
        this.$swal({ type: 'error', title: 'ไฟล์ใหญ่เกินไป', text: file.name + ' — ไฟล์ต้องไม่เกิน 5 MB (' + (file.size / 1024 / 1024).toFixed(1) + ' MB)' })
        return false
      }
      return true
    },
    onFileChange (row, lang, file) {
      if (!this.checkFileSize(file)) return
      const cell = this.getCell(row, lang)
      const previewUrl = URL.createObjectURL(file)
      this.previewUrls.push(previewUrl)
      this.setCell(row, lang, { url: cell.url, fileName: cell.fileName, file: file, previewUrl: previewUrl, dirty: true })
    },
    removeCell (row, lang) {
      const cell = this.getCell(row, lang)
      if (cell.file && !cell.url) { this.restoreCell(row, lang); return }
      this.setCell(row, lang, { removed: true, dirty: true })
    },
    restoreCell (row, lang) {
      const saved = this.savedSounds[row.key] && this.savedSounds[row.key][lang]
      this.setCell(row, lang, saved ? Object.assign({}, saved) : {})
    },
    // ── เลขคิว A–Z 0–9
    charTileClass (ch) {
      const cell = this.getCell(this.charRow(ch), this.activeLang)
      if (cell.file) return 'char-tile--pending'
      if (cell.url) return 'char-tile--done'
      if (cell.removed) return 'char-tile--removed'
      return ''
    },
    charIcon (ch) {
      const cell = this.getCell(this.charRow(ch), this.activeLang)
      if (cell.file) return 'mdi-circle-medium'
      if (cell.url) return 'mdi-volume-high'
      if (cell.removed) return 'mdi-delete-clock-outline'
      return 'mdi-upload-outline'
    },
    charTitle (ch) {
      const cell = this.getCell(this.charRow(ch), this.activeLang)
      if (cell.file) return cell.file.name + ' · ยังไม่บันทึก (กดเพื่อฟัง/เปลี่ยน)'
      if (cell.url) return (cell.fileName || 'มีเสียง') + ' (กดเพื่อฟัง/เปลี่ยน)'
      if (cell.removed) return 'จะลบเมื่อบันทึก (กดเพื่อยกเลิก/เปลี่ยน)'
      return 'ยังไม่มี — กดเพื่ออัปโหลด'
    },
    charHasMenu (ch) {
      const cell = this.getCell(this.charRow(ch), this.activeLang)
      return !!(cell.url || cell.file || cell.removed)
    },
    onCharClick (ch) {
      if (this.charHasMenu(ch)) return
      this.pickCharFile(ch)
    },
    pickCharFile (ch) {
      this.pendingCharRow = this.charRow(ch)
      this.matchScope = 'queue_char'
      this.$refs.multiInput.click()
    },
    pickMulti (scope) {
      this.pendingCharRow = null
      this.matchScope = scope
      this.$refs.multiInput.click()
    },
    onMultiFiles (event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      if (!files.length) return
      if (this.pendingCharRow && files.length === 1) {
        // มาจากการกดตัวอักษรเดียว → ใส่ตรง ๆ ไม่ต้องจับคู่
        this.onFileChange(this.pendingCharRow, this.activeLang, files[0])
        this.pendingCharRow = null
        return
      }
      this.pendingCharRow = null
      this.matchList = files.filter(f => this.checkFileSize(f)).map(file => ({ file: file, key: this.guessTarget(file.name) }))
      this.dialogMatch = true
    },
    // เดาเป้าหมายจากชื่อไฟล์: "A.mp3" → ตัว A · "ห้องตรวจ 1.mp3" → ห้อง · "prefix.mp3"/"ขอเชิญคิว.mp3" → ท่อนเปิด
    guessTarget (fileName) {
      const stem = String(fileName || '').replace(/\.[^.]+$/, '').trim()
      const norm = s => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase()
      if (this.matchScope === 'queue_char') {
        const ch = stem.toUpperCase()
        return QUEUE_CHARS.indexOf(ch) > -1 ? 'queue_char:' + ch + ':' : null
      }
      if (['prefix', 'ขอเชิญคิว', 'ท่อนเปิด'].indexOf(norm(stem)) > -1) return 'prefix:prefix:'
      if (['connector', 'ที่ห้อง', 'ที่', 'เชิญที่'].indexOf(norm(stem)) > -1) return 'connector:connector:'
      const rooms = this.allRoomRows
      // ห้องในแผนกที่เลือกอยู่ก่อน แล้วค่อยแผนกอื่น
      const ordered = rooms.filter(r => r.sourceId === String(this.selectedFlowId)).concat(rooms.filter(r => r.sourceId !== String(this.selectedFlowId)))
      const hit = ordered.filter(r => norm(r.label) === norm(stem) || (r.labelEn && norm(r.labelEn) === norm(stem)))[0]
      return hit ? hit.key : null
    },
    applyMatches () {
      this.matchList.filter(m => m.key).forEach(m => {
        const parts = m.key.split(':')
        const row = this.buildRow(parts[0], parts[1], parts.slice(2).join(':'), '')
        this.onFileChange(row, this.activeLang, m.file)
      })
      this.dialogMatch = false
      this.matchList = []
    },
    // ── ภาษา
    async addLang () {
      const code = String(this.newLangCustom || this.newLang || '').trim().toLowerCase()
      if (this.newLangBookingNoError) return
      this.dialogLang = false
      if (!code || ['th', 'en'].indexOf(code) > -1) return
      if (this.langs.indexOf(code) === -1) this.langs.push(code)
      this.$set(this.langBookingNo, code, String(this.newLangBookingNo || '').trim())
      this.newLang = null
      this.newLangCustom = ''
      this.newLangBookingNo = ''
      await this.setMode(this.mode)
      this.activeLang = code
    },
    async removeLang (code) {
      const label = (this.langColumns.filter(lang => lang.code === code)[0] || {}).label || code
      const keys = Object.keys(this.savedSounds).filter(key => this.savedSounds[key][code] && this.savedSounds[key][code].url)
      const pending = Object.keys(this.sounds).filter(key => this.sounds[key][code] && this.sounds[key][code].dirty).length
      const text = 'เสียงภาษานี้ ' + keys.length + ' ไฟล์จะถูกลบ และจอจะไม่ใช้ภาษานี้อีก' + (pending ? ' (รวมที่ยังไม่บันทึก ' + pending + ' รายการ)' : '')
      if (!await this.confirmSwal({ title: 'ลบภาษา ' + label + '?', text: text, type: 'warning' })) return
      const keepFlow = this.selectedFlowId
      this.dataReady = false
      try {
        if (keys.length) {
          const lang = keys.map(key => {
            const parts = key.split(':')
            return { sourceType: parts[0], sourceId: parts[1], servicePointKey: parts.slice(2).join(':'), langCode: code, soundUrl: null }
          })
          const response = await axios.post(this.$DNS_IP + '/service_point_sound/save', { shopId: this.shopId, main: [], lang: lang, LAST_USER: this.userName })
          if (!response.data || response.data.status !== true) throw new Error((response.data && response.data.message) || 'remove failed')
        }
        this.langs = this.langs.filter(item => item !== code)
        this.$delete(this.langBookingNo, code)
        await this.setMode(this.mode)
        if (this.activeLang === code) this.activeLang = 'th'
        await this.getData()
        this.selectedFlowId = keepFlow
        this.$swal({ type: 'success', title: 'สำเร็จ', text: 'ลบภาษา ' + label + ' แล้ว' })
      } catch (error) {
        console.log('removeLang error', error)
        this.$swal({ type: 'error', title: 'ลบภาษาไม่สำเร็จ', text: String(error.message || error) })
        await this.getData()
        this.selectedFlowId = keepFlow
      }
    },
    // ── เล่นเสียง (ท่อนที่ร้านไม่มี → เสียงกลาง เหมือนที่จอทำ)
    cellUrl (row, lang) {
      const cell = this.getCell(row, lang)
      return cell.previewUrl || cell.url || ''
    },
    // ห้องชื่อเดียวกันในแผนกอื่นที่มีไฟล์ (จอ TV ก็ยืมแบบเดียวกัน เรียงตาม key)
    sharedRoom (row, lang) {
      if (row.sourceType !== 'flow' || this.cellHas(row, lang)) return null
      return this.allRoomRows
        .filter(r => r.sourceType === 'flow' && r.key !== row.key && r.label === row.label && this.cellHas(r, lang))
        .sort((a, b) => (a.key < b.key ? -1 : 1))[0] || null
    },
    sharedRoomLabel (row, lang) {
      const shared = this.sharedRoom(row, lang)
      return shared ? 'ใช้ไฟล์ของแผนก ' + shared.flowName : ''
    },
    roomUrl (row, lang) {
      const own = this.cellUrl(row, lang)
      if (own) return own
      const shared = this.sharedRoom(row, lang)
      return shared ? this.cellUrl(shared, lang) : ''
    },
    systemLang (lang) { return lang === 'en' ? 'en' : 'th' },
    prefixUrl (lang) { return this.cellUrl(this.prefixRow, lang) || SYSTEM_SOUND.prefix[this.systemLang(lang)] },
    queueUrls (code, lang) {
      const chars = String(code || '').toUpperCase().split('')
      if (chars.length && chars.every(ch => this.cellHas(this.charRow(ch), lang))) return chars.map(ch => this.cellUrl(this.charRow(ch), lang))
      return [SYSTEM_SOUND.queue[this.systemLang(lang)](String(code || 'A001').toUpperCase())]
    },
    playList (urls) {
      const player = this.$refs.player
      if (!player) return
      const list = urls.filter(url => url)
      let index = 0
      const next = () => {
        if (index >= list.length) return
        player.src = list[index++]
        player.load()
        player.play().catch(error => console.log('play error', error))
      }
      player.onended = next
      next()
    },
    playCell (row, lang) { this.playList([this.roomUrl(row, lang)]) },
    roomPartUrls (row, lang) {
      const room = row ? this.roomUrl(row, lang) : ''
      return room ? [this.cellUrl(this.connectorRow, lang), room] : []
    },
    testSequence (row, lang) { this.playList([this.prefixUrl(lang)].concat(this.queueUrls('A001', lang), this.roomPartUrls(row, lang))) },
    playSample () {
      const lang = this.activeLang
      const roomRow = this.allRoomRows.filter(r => r.key === this.sampleRoomKey)[0]
      this.playList([this.prefixUrl(lang)].concat(this.queueUrls(this.sampleQueue, lang), this.roomPartUrls(roomRow, lang)))
    },
    // ── บันทึก
    async uploadFile (file) {
      const params = new FormData()
      params.append('file', file)
      params.append('shopId', this.shopId)
      const response = await axios.post(this.$DNS_IP + '/file/upload/queueSound', params)
      if (!response.data || response.data.status !== true) throw new Error((response.data && response.data.message) || 'upload failed')
      return response.data
    },
    async save () {
      if (!await this.confirmSwal({ title: 'ต้องการ บันทึกข้อมูล ใช่หรือไม่?', text: 'ค้าง ' + this.dirtyCount + ' รายการ' })) return
      this.dataReady = false
      try {
        const main = []
        const lang = []
        for (const key of Object.keys(this.sounds)) {
          const cells = this.sounds[key]
          const dirtyLangs = Object.keys(cells).filter(code => cells[code].dirty)
          if (!dirtyLangs.length) continue
          for (const code of dirtyLangs) {
            if (cells[code].file) {
              const uploaded = await this.uploadFile(cells[code].file)
              cells[code].url = uploaded.url
              cells[code].fileName = uploaded.fileName
            }
          }
          const parts = key.split(':')
          const base = { sourceType: parts[0], sourceId: parts[1], servicePointKey: parts.slice(2).join(':') }
          const names = {}
          Object.keys(cells).forEach(code => { if (cells[code].url && cells[code].fileName) names[code] = cells[code].fileName })
          main.push(Object.assign({}, base, {
            soundTh: (cells.th && cells.th.url) || null,
            soundEn: (cells.en && cells.en.url) || null,
            soundFileNameAll: JSON.stringify(names)
          }))
          dirtyLangs.filter(code => code !== 'th' && code !== 'en').forEach(code => {
            lang.push(Object.assign({}, base, { langCode: code, soundUrl: cells[code].url || null }))
          })
        }
        const response = await axios.post(this.$DNS_IP + '/service_point_sound/save', { shopId: this.shopId, main: main, lang: lang, LAST_USER: this.userName })
        if (!response.data || response.data.status !== true) throw new Error((response.data && response.data.message) || 'save failed')
        const keepFlow = this.selectedFlowId
        const keepLang = this.activeLang
        await this.getData()
        this.selectedFlowId = keepFlow
        this.activeLang = keepLang
        this.$swal({ type: 'success', title: 'สำเร็จ', text: 'บันทึกสำเร็จ' })
      } catch (error) {
        console.log('save error', error)
        this.dataReady = true
        this.$swal({ type: 'error', title: 'บันทึกไม่สำเร็จ', text: String(error.message || error) })
      }
    },
    // ── โหมด
    async onModeToggle (value) {
      if (!value || value === this.mode) return
      await this.toggleMode(value === 'import')
    },
    async toggleMode (checked) {
      const nextMode = checked ? 'import' : 'legacy'
      const text = checked
        ? (this.missingRoomCount ? 'ยังมี ' + this.missingRoomCount + ' ห้องที่ไม่มีเสียงไทย จอจะประกาศแค่ท่อนเปิด + เลขคิวสำหรับห้องเหล่านั้น' : 'จอจะเปลี่ยนไปใช้เสียงที่ร้านอัปโหลดทันที')
        : 'จอจะกลับไปใช้เสียงกลางของระบบ ไฟล์ที่อัปโหลดไว้ยังอยู่'
      const confirmed = await this.confirmSwal({ title: checked ? 'เปิดใช้เสียงของร้าน?' : 'กลับไปใช้เสียงกลางของระบบ?', text: text })
      if (!confirmed) {
        this.modeToggleKey++
        return
      }
      await this.setMode(nextMode)
    },
    async setMode (mode) {
      try {
        const response = await axios.post(this.$DNS_IP + '/service_point_sound/set_mode', {
          shopId: this.shopId, queueSoundMode: mode, queueSoundLangs: this.queueSoundLangsValue, LAST_USER: this.userName
        })
        if (!response.data || response.data.status !== true) throw new Error((response.data && response.data.message) || 'set_mode failed')
        this.mode = mode
        const session = this.$session.getAll().data || {}
        session.queueSoundMode = mode
        session.queueSoundLangs = this.queueSoundLangsValue
        this.$session.set('data', session)
      } catch (error) {
        console.log('setMode error', error)
        this.$swal({ type: 'error', title: 'อัปเดตโหมดไม่สำเร็จ', text: String(error.message || error) })
        await this.getData()
      }
    }
  }
}
</script>

<style scoped>
.queue-sound-setting { font-variant-numeric: tabular-nums; }

/* ปุ่มเลือกโหมด: สี่เหลี่ยม 2 ช่อง */
.mode-toggle .v-btn { text-transform: none; letter-spacing: 0; }

/* สูตรประกาศ 4 ท่อน */
.announce-formula { display: flex; align-items: center; flex-wrap: wrap; }
.announce-formula__part { display: inline-block; padding: 4px 12px; border-radius: 16px; background: #e8eef7; color: #1a3a7a; font-size: 13px; font-weight: 500; }
.announce-formula__plus { margin: 0 8px; color: rgba(0, 0, 0, 0.38); }

/* section ต่อท่อน: หัว (เลข + ชื่อ + คำอธิบาย) / เนื้อ เว้นซ้ายเท่ากับหัว */
.segment__head { display: flex; align-items: flex-start; gap: 12px; }
.segment__body { margin-top: 12px; padding-left: 44px; }
.segment__no { flex: 0 0 auto; width: 32px; height: 32px; border-radius: 50%; background: #1a3a7a; color: #fff; font-weight: 600; font-size: 14px; display: inline-flex; align-items: center; justify-content: center; }
.segment__no--sm { width: 22px; height: 22px; font-size: 12px; }

/* ตาราง A–Z 0–9 */
.char-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(56px, 1fr)); gap: 8px; max-width: 760px; }
.char-tile { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px; height: 52px; border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 6px; background: #fff; cursor: pointer; transition: transform 120ms ease-out, box-shadow 120ms ease-out; }
.char-tile:hover { box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16); transform: translateY(-1px); }
.char-tile:focus-visible { outline: 2px solid #1976d2; outline-offset: 1px; }
.char-tile__char { font-size: 16px; font-weight: 600; line-height: 1; color: rgba(0, 0, 0, 0.6); }
.char-tile__icon { color: rgba(0, 0, 0, 0.38) !important; }
.char-tile--done { border-color: #4caf50; background: #f1f8f1; }
.char-tile--done .char-tile__char { color: #2e7d32; }
.char-tile--done .char-tile__icon { color: #4caf50 !important; }
.char-tile--pending { border-color: #ff9800; background: #fff8ee; }
.char-tile--pending .char-tile__icon { color: #ff9800 !important; }
.char-tile--removed { border-color: #ef5350; background: #fff5f5; }

/* ตารางห้อง */
.flow-select { max-width: 360px; }
.sound-table th { white-space: nowrap; color: rgba(0, 0, 0, 0.6); font-size: 12px; }
.sound-table th:first-child, .sound-table td:first-child { width: 40%; min-width: 180px; }
.sound-table td { vertical-align: middle; padding-top: 8px !important; padding-bottom: 8px !important; }
.row-orphan td { background: #fff5f5; }

/* ช่องเสียง */
.sound-cell { display: flex; align-items: center; flex-wrap: wrap; gap: 0 12px; }
.sound-cell__actions { margin-left: -6px; white-space: nowrap; }
.sound-cell__status { font-size: 12px; line-height: 18px; max-width: 260px; }
</style>



// WEBPACK FOOTER //
// src/components/BeFood/setting_queue_sound.vue